import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useRouteMatch, useLocation } from 'react-router-dom';
import type { OrgFeatures } from '@bb/domain/dist/types';
import type { Feature } from '@bb/automaton';
import type { Listing } from 'api/ListingController/getListing';

const FEATURE_ROUTE_MAP: Record<string, Array<Feature | `${Feature}`>> = {
  '/listing/:listingId/offer/': ['OFFERS'],
  '/listing/:listingId/offer/new': ['OFFERS'],
  '/listing/:listingId/offer_settings': ['OFFERS'],
  '/listing/:listingId/tour/:tourId/details': ['VIRTUAL_SHOWINGS'],
};

// should be enough to decide if it's object id by length
const getObjectIdOrUndefined = (id: string | undefined) =>
  id?.match(/^[0-9a-fA-F]{24}$/) ? id : undefined;

const useListingRedirect = () => {
  const [fetching, setFetching] = useState(true);
  const [listing, setListing] = useState<Listing>();
  const [unauthorizedByListing, setUnauthorizedByListing] = useState(false);
  const { pathname: originalPath } = useLocation();
  const match = useRouteMatch<{ listingId?: string }>({
    path: '/listing/:listingId',
  });
  const tourMatch = useRouteMatch<{ listingId?: string; tourId?: string }>({
    path: '/listing/:listingId/tour/:tourId',
  });
  const listingId = getObjectIdOrUndefined(
    (tourMatch ?? match)?.params.listingId,
  );
  const tourId = getObjectIdOrUndefined(tourMatch?.params.tourId);
  const path = useMemo(
    () =>
      originalPath.replace(listingId, ':listingId').replace(tourId, ':tourId'),
    [originalPath],
  );

  const fetch = async () => {
    // not listing endpoint
    if (!listingId) return setFetching(false);

    // not in the feature protected endpoint list
    const requiredFeatures = FEATURE_ROUTE_MAP[path];
    if (!requiredFeatures) return setFetching(false);

    // check if already fetched this listing
    setFetching(true);
    let unauthorized = true;
    if (listing?.id === listingId) {
      // hack to make react stop here for a sec
      await Promise.resolve();
      unauthorized = requiredFeatures.some(
        feature => !listing.org.features.includes(feature),
      );
    } else {
      const { data: fetchedListing } = await axios.get<Listing>(
        `/listing/${listingId}`,
      );
      setListing(fetchedListing);
      unauthorized = requiredFeatures.some(
        feature => !fetchedListing.org.features.includes(feature),
      );
    }
    setUnauthorizedByListing(unauthorized);
    setFetching(false);
  };

  useEffect(() => {
    fetch();
  }, [path]);

  return {
    fetching,
    unauthorizedByListing,
  };
};

export default useListingRedirect;
